import { ReactElement } from 'react';
import { Chip, Tooltip, Box, Typography, ChipProps } from '@mui/material';
import {
  CheckCircle,
  PauseCircle,
  Error,
  HourglassEmpty,
  Archive,
  Block,
  Warning,
  CloudOff,
} from '@mui/icons-material';
import { getStatusInfo, isPausedByParent, hasIssues } from '../constants/status';

interface StatusBadgeProps {
  /**
   * The local status (user intent): ACTIVE, PAUSED, etc.
   */
  status: string;
  /**
   * The effective status from Facebook (reality).
   * If null, the entity is a draft (not yet published).
   */
  effectiveStatus?: string | null;
  /** A parent changed and the platform has not confirmed what that did here yet. */
  staleSince?: string | null;
  /**
   * Size of the chip
   */
  size?: ChipProps['size'];
  /**
   * Show icon alongside the status text
   */
  showIcon?: boolean;
}

const statusIcons: Record<string, ReactElement> = {
  ACTIVE: <CheckCircle fontSize="small" />,
  IN_PROCESS: <HourglassEmpty fontSize="small" />,
  PAUSED: <PauseCircle fontSize="small" />,
  CAMPAIGN_PAUSED: <PauseCircle fontSize="small" />,
  ADSET_PAUSED: <PauseCircle fontSize="small" />,
  PENDING_REVIEW: <HourglassEmpty fontSize="small" />,
  PENDING_BILLING_INFO: <Warning fontSize="small" />,
  PREAPPROVED: <HourglassEmpty fontSize="small" />,
  DISAPPROVED: <Block fontSize="small" />,
  WITH_ISSUES: <Error fontSize="small" />,
  ARCHIVED: <Archive fontSize="small" />,
  DELETED: <Block fontSize="small" />,
};

/**
 * StatusBadge displays the status of an entity with context-aware tooltips.
 *
 * For published entities (has effectiveStatus), it shows:
 * - The effective_status with an explanation of why
 * - Color-coded to indicate if action is needed
 *
 * For draft entities (no effectiveStatus), it shows:
 * - The local status, marked unconfirmed until the import reports back
 *
 * The tooltip explains:
 * - What the status means
 * - Why it's in that state (e.g., "Campaign is paused")
 * - What action to take if needed
 */
export default function StatusBadge({
  status,
  effectiveStatus,
  staleSince,
  size = 'small',
  showIcon = false,
}: StatusBadgeProps) {
  // A totals row has neither, and a status chip on a sum means nothing.
  if (!status && !effectiveStatus) return null;

  // Everything here exists on Facebook; only a just-created row has yet to be reported on.
  if (!effectiveStatus) {
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Not yet confirmed
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Created on Facebook. The next import will say what it is doing.
            </Typography>
          </Box>
        }
        arrow
      >
        <Chip
          icon={showIcon ? <CloudOff fontSize="small" /> : undefined}
          label={`${status} · confirming`}
          size={size}
          color="default"
          variant="outlined"
        />
      </Tooltip>
    );
  }

  const info = getStatusInfo(effectiveStatus);
  const icon = statusIcons[effectiveStatus];

  // Build tooltip content
  const tooltipContent = (
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {info.label}
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.9 }}>
        {info.description}
      </Typography>
      {/* Show local status if different from effective */}
      {status !== effectiveStatus && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
          Your setting: {status}
        </Typography>
      )}
      {/* Show action hint for issues */}
      {hasIssues(effectiveStatus) && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'warning.light' }}>
          Action required
        </Typography>
      )}
      {isPausedByParent(effectiveStatus) && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'info.light' }}>
          Activate parent to resume
        </Typography>
      )}
      {staleSince && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'warning.light' }}>
          A parent changed; this is what the platform last said. Confirmed within 30 minutes.
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow>
      <Chip
        icon={showIcon && icon ? icon : undefined}
        label={staleSince ? `${info.label} · confirming` : info.label}
        size={size}
        color={info.color}
        variant={staleSince ? 'outlined' : 'filled'}
      />
    </Tooltip>
  );
}
